// Knowledge-graph API client. Shares the axios instance in api.js, so the
// bearer token and the base URL are attached by the existing interceptor.
import axios from "axios";
import { API_BASE_URL, TOKEN_KEY } from "./api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const graphApi = {
  // One batch of nodes for the table, plus the total row count.
  // `label` filters to one subject; a node carrying several matches each of them.
  listNodes: async (limit = 15, offset = 0, sort = "id", order = "asc", label = null) =>
    (await api.get("/api/graph/nodes", {
      params: { limit, offset, sort, order, ...(label ? { label } : {}) },
    })).data,

  // Every subject in use, with how many nodes carry it.
  getLabels: async () => (await api.get("/api/graph/labels")).data,

  // The whole graph in dependency layers, with its edges. `label` draws one
  // subject only.
  getOrder: async (label = null) =>
    (await api.get("/api/graph/order", { params: label ? { label } : {} })).data,

  // One node's prerequisite closure, same shape as getOrder.
  getNodeOrder: async (slug) => (await api.get(`/api/graph/order/${slug}`)).data,

  // What to read next. The reader's position comes from the visit log on the
  // server, so nothing about the path is sent from here.
  next: async (label = null) =>
    (await api.post("/api/graph/next", null, { params: label ? { label } : {} })).data,

  // The node read before this one.
  prev: async () => (await api.get("/api/graph/prev")).data,

  // Where the reader is in the current path, and whether Prev has anywhere to go.
  path: async () => (await api.get("/api/graph/path")).data,

  // Landing on the bare graph starts a new path.
  resetPath: async () => (await api.post("/api/graph/path/reset")).data,

  // Soft-delete a node, or bring it back. Nothing is destroyed either way.
  deleteNode: async (slug) =>
    (await api.post(`/api/graph/nodes/${slug}/delete`)).data,
  restoreNode: async (slug) =>
    (await api.post(`/api/graph/nodes/${slug}/restore`)).data,

  // Answer "did you still know it?" for the reading in progress.

  // Append one vote (true) or unvote (false) to a node's log.
  vote: async (slug, field, value) =>
    (await api.post(`/api/graph/nodes/${slug}/vote`, { field, vote: value })).data,

  // One node's content plus its edges in both directions.
  // `via` records HOW the reader got here, which is what makes the back-stack
  // replayable on the server: a "prev" pops where everything else pushes.
  getNode: async (slug, via = "click", mode = null) =>
    (await api.get(`/api/graph/nodes/${slug}`, { params: { via, mode } })).data,

  // The last sync's report: dangling links, rejected edges, counts.
  getHealth: async () => (await api.get("/api/graph/health")).data,

  // The repetition curves: nodes read at least k times, by day. `days` trims
  // the window shown; the totals are always accumulated from the first reading.
  getProgress: async (days = 0) =>
    (await api.get("/api/graph/progress", { params: { days } })).data,
};

export default graphApi;
